import type { MockScenario } from '../shared/mock'
import type { Place } from '../shared/contract'
import { ENGINE_AVAILABLE } from './data/engineLoader'

/**
 * shared/mock.ts's MOCK_SCENARIOS, copied so the scenario generator itself stays out of the
 * main bundle (it loads with the demo data; tests/app-data.test.tsx keeps the copy in sync).
 */
export const SCENARIO_NAMES: readonly MockScenario[] = ['clear', 'partly', 'cloudy', 'rain-incoming', 'rain', 'marginal', 'storm', 'snow', 'fog', 'smoke', 'night', 'sunset']

/** `?mock=<scenario>` switches the whole app to shared synthetic data (visibly labelled). */
export function readMockScenario(search = typeof location !== 'undefined' ? location.search : ''): MockScenario | null {
  const v = new URLSearchParams(search).get('mock')
  if (v == null) return null
  return (SCENARIO_NAMES as readonly string[]).includes(v) ? (v as MockScenario) : 'partly'
}

/** `?mock=` forces demo data; without the engine bundled the app also falls back to it. */
export const MOCK_SCENARIO: MockScenario | null = readMockScenario() ?? (ENGINE_AVAILABLE ? null : 'partly')
export const IS_MOCK = MOCK_SCENARIO != null

/**
 * `?mockDelay=<ms>` (demo only, capped at 5 s): the demo forecast arrives that much later, so the first paint is the skeleton and the
 * fill-in is the one a real cold load has. Real-data runs are capped by Open-Meteo's daily quota; this lets the real-browser checker
 * (docs/QA_REAL_BROWSER.md) measure the pending -> ready move as often as it likes.
 */
export const MOCK_DELAY_MS = IS_MOCK ? Math.min(5000, Math.max(0, Number(new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get('mockDelay')) || 0)) : 0

/** Demo places used only in mock mode, each paired with a scene so the list shows variety. */
export const DEMO_PLACES: (Place & { scenario: MockScenario | 'url' })[] = [
  { name: 'Wilkes-Barre', region: 'PA', label: 'Wilkes-Barre, PA', latitude: 41.2459, longitude: -75.8813, zip: '18702', scenario: 'url' },
  { name: 'Philadelphia', region: 'PA', label: 'Philadelphia, PA', latitude: 39.9526, longitude: -75.1652, zip: '19102', scenario: 'sunset' },
  { name: 'New York', region: 'NY', label: 'New York, NY', latitude: 40.7128, longitude: -74.006, zip: '10007', scenario: 'night' },
  { name: 'Denver', region: 'CO', label: 'Denver, CO', latitude: 39.7392, longitude: -104.9903, zip: '80202', scenario: 'snow' },
  { name: 'Miami', region: 'FL', label: 'Miami, FL', latitude: 25.7617, longitude: -80.1918, zip: '33131', scenario: 'storm' },
]

/** A small offline gazetteer so search works in mock mode. Real mode uses /v2/places. */
export const DEMO_GAZETTEER: Place[] = [
  ...DEMO_PLACES.map(({ scenario: _s, ...p }) => p),
  { name: 'Scranton', region: 'PA', label: 'Scranton, PA', latitude: 41.4089, longitude: -75.6624, zip: '18503' },
  { name: 'Pittsburgh', region: 'PA', label: 'Pittsburgh, PA', latitude: 40.4406, longitude: -79.9959, zip: '15222' },
  { name: 'Harrisburg', region: 'PA', label: 'Harrisburg, PA', latitude: 40.2732, longitude: -76.8867, zip: '17101' },
  { name: 'Allentown', region: 'PA', label: 'Allentown, PA', latitude: 40.6023, longitude: -75.4714, zip: '18101' },
  { name: 'Boston', region: 'MA', label: 'Boston, MA', latitude: 42.3601, longitude: -71.0589, zip: '02108' },
  { name: 'Chicago', region: 'IL', label: 'Chicago, IL', latitude: 41.8781, longitude: -87.6298, zip: '60602' },
  { name: 'Seattle', region: 'WA', label: 'Seattle, WA', latitude: 47.6062, longitude: -122.3321, zip: '98101' },
  { name: 'San Francisco', region: 'CA', label: 'San Francisco, CA', latitude: 37.7749, longitude: -122.4194, zip: '94102' },
  { name: 'Los Angeles', region: 'CA', label: 'Los Angeles, CA', latitude: 34.0522, longitude: -118.2437, zip: '90012' },
  { name: 'Austin', region: 'TX', label: 'Austin, TX', latitude: 30.2672, longitude: -97.7431, zip: '78701' },
  { name: 'Phoenix', region: 'AZ', label: 'Phoenix, AZ', latitude: 33.4484, longitude: -112.074, zip: '85003' },
  { name: 'Atlanta', region: 'GA', label: 'Atlanta, GA', latitude: 33.749, longitude: -84.388, zip: '30303' },
  { name: 'Minneapolis', region: 'MN', label: 'Minneapolis, MN', latitude: 44.9778, longitude: -93.265, zip: '55401' },
  { name: 'Anchorage', region: 'AK', label: 'Anchorage, AK', latitude: 61.2181, longitude: -149.9003, zip: '99501' },
  { name: 'Honolulu', region: 'HI', label: 'Honolulu, HI', latitude: 21.3069, longitude: -157.8583, zip: '96813' },
]

/** Offline search over the demo gazetteer (demo mode only). */
export function demoSearch(q: string): Place[] {
  const s = q.trim().toLowerCase()
  if (!s) return []
  return DEMO_GAZETTEER.filter(
    (p) => p.label.toLowerCase().includes(s) || (p.zip ?? '').startsWith(s),
  ).slice(0, 8)
}

/** Scenario for a place in mock mode: demo places keep their scene, others hash to one. */
export function scenarioFor(latitude: number, longitude: number, fallback: MockScenario): MockScenario {
  const demo = DEMO_PLACES.find(
    (p) => Math.abs(p.latitude - latitude) < 0.01 && Math.abs(p.longitude - longitude) < 0.01,
  )
  if (demo) return demo.scenario === 'url' ? fallback : demo.scenario
  const pool: MockScenario[] = ['partly', 'clear', 'cloudy', 'rain-incoming', 'rain', 'sunset', 'fog']
  const h = Math.abs(Math.round(latitude * 1000) * 31 + Math.round(longitude * 1000))
  return pool[h % pool.length]
}
